import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { detectInAppBrowser } from './InAppBrowserBlocker';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSSafari, setIsIOSSafari] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Don't show in in-app browsers
    if (detectInAppBrowser()) return;

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    setIsIOS(iOS);

    // Detect iOS + Safari specifically (not Chrome on iOS, not in-app browser)
    // Safari on iOS does NOT have 'CriOS' (Chrome) or 'FxiOS' (Firefox) or 'EdgiOS' (Edge)
    const isSafariOnIOS = iOS && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    setIsIOSSafari(isSafariOnIOS);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowInstallBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS instructions ONLY in Safari on iOS
    if (isSafariOnIOS && !localStorage.getItem('pwa-ios-dismissed')) {
      setTimeout(() => setShowInstallBanner(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    if (isIOS) localStorage.setItem('pwa-ios-dismissed', 'true');
  };

  if (isInstalled || !showInstallBanner) return null;

  // iOS Safari share icon SVG (matches Apple's actual share icon)
  const AppleShareIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle mx-0.5">
      <path d="M12 2L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M8 6L12 2L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M4 14V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-zinc-900 border border-primary/30 rounded-2xl p-4 shadow-xl z-50 animate-slide-up" data-testid="install-pwa-banner">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-white transition-colors"
        data-testid="dismiss-install-banner"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-black flex items-center justify-center flex-shrink-0 border border-primary/20">
          <Smartphone className="w-7 h-7 text-primary" />
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-white text-lg">Installer YUMIX</h3>

          {isIOSSafari ? (
            <div className="mt-2 text-sm text-zinc-400">
              <p>Pour installer sur iPhone/iPad :</p>
              <ol className="mt-2 space-y-1.5 text-xs">
                <li className="flex items-center gap-1">
                  1. Appuie sur <span className="text-white font-medium">Partager</span> <AppleShareIcon />
                </li>
                <li>2. Fais defiler et choisis <span className="text-white font-medium">"Sur l'ecran d'accueil"</span></li>
                <li>3. Appuie sur <span className="text-white font-medium">Ajouter</span></li>
              </ol>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-400 mt-1">
                Acces rapide depuis ton ecran d'accueil
              </p>
              {deferredPrompt && (
                <button
                  onClick={handleInstallClick}
                  className="mt-3 w-full flex items-center justify-center gap-2 bg-primary text-black font-semibold py-2.5 px-4 rounded-xl hover:bg-primary/90 transition-colors"
                  data-testid="install-pwa-btn"
                >
                  <Download className="w-5 h-5" />
                  Installer l'application
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
